// Import React and necessary components from Shadcn UI or your specific UI library
import React from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardHeader, CardContent, CardFooter } from "./ui/card";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Separator } from "./ui/separator";
import { CheckedState } from "@radix-ui/react-checkbox";
import { ModulusCheck } from "./services/mandate.service";

interface DirectDebitFormProps {
  handleBackButton?: () => void;
  handleSubmitButton: () => void;
  data: any;
  setData: React.Dispatch<React.SetStateAction<any>>;
  disableFormCard: boolean;
  modulusCheck?: ModulusCheck;
}

const DirectDebitForm: React.FC<DirectDebitFormProps> = ({
  handleBackButton,
  handleSubmitButton,
  data,
  setData,
  disableFormCard,
  modulusCheck,
}) => {
  const [checked, setChecked] = React.useState(data.terms);

  // Helper function to format bank address from modulusCheck
  const formatBankAddress = (modCheck?: ModulusCheck): string => {
    if (!modCheck || modCheck.checked === false) {
      return "";
    }

    const parts: string[] = [];
    
    if (modCheck.bankName) parts.push(modCheck.bankName);
    if (modCheck.branchAddress1) parts.push(modCheck.branchAddress1);
    if (modCheck.branchAddress2) parts.push(modCheck.branchAddress2);
    if (modCheck.branchAddress3) parts.push(modCheck.branchAddress3);
    if (modCheck.branchAddress4) parts.push(modCheck.branchAddress4);
    if (modCheck.branchAddressTown) parts.push(modCheck.branchAddressTown);
    if (modCheck.branchAddressPostCode) parts.push(modCheck.branchAddressPostCode);

    return parts.filter(part => part.trim() !== "").join(", ");
  };

  // Update bankAddress when modulusCheck data is available
  React.useEffect(() => {
    if (modulusCheck && modulusCheck.checked === true) {
      const formattedAddress = formatBankAddress(modulusCheck);
      if (formattedAddress && !data.bankAddress) {
        setData((prev: any) => ({ ...prev, bankAddress: formattedAddress }));
      }
    }
  }, [modulusCheck, data.bankAddress, setData]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | CheckedState,
    id?: string
  ) => {
    if (typeof e === "boolean" || e === "indeterminate") {
      // Handle checkbox - convert "indeterminate" to false
      setData((prev: any) => ({ ...prev, [id!]: e === true }));
      setChecked(e === true);
    } else {
      // Handle regular input
      const { id, value } = e.target;
      setData((prev: any) => ({ ...prev, [id]: value }));
    }
  };

  return (
    <Card
      className={`text-left w-full max-w-3xl h-fit ${
        disableFormCard ? "opacity-50" : "opacity-100"
      }`}
    >
      <CardHeader>
        <div className="grid grid-cols-2 gap-10">
          <div className="flex flex-col">
            <h1 className="text-2xl mb-2">{data.originatorCompanyName}</h1>
            <p className="text-md">{data.originatorCompanyAddress1}</p>
            <p className="text-md">{data.originatorCompanyAddress2}</p>
          </div>
          <div className="flex flex-col">
            <h1 className="max-w-72 text-xl text-black">
              Instruction to your Bank or Building Society to pay by Direct
              Debit
            </h1>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-10">
          <div className="flex flex-col gap-4">
            <div className="">
              <Label htmlFor="name">Company Name</Label>
              <Input
                type="text"
                disabled={true}
                value={data.name}
                onChange={handleChange}
                id="name"
              />
            </div>
            <div className="">
              <Label htmlFor="email">Company Email</Label>
              <Input
                type="text"
                disabled={true}
                value={data.email}
                onChange={handleChange}
                id="email"
              />
            </div>
            <div className="">
              <Label htmlFor="accountHolderName">Name of Account Holder</Label>
              <Input
                type="text"
                disabled={false}
                value={data.accountHolderName}
                onChange={handleChange}
                id="accountHolderName"
              />
            </div>
            <div className="">
              <Label htmlFor="bankAccountNo">Bank Account Number</Label>
              <Input
                type="text"
                disabled={true}
                value={data.bankAccountNo}
                onChange={handleChange}
                id="bankAccountNo"
              />
            </div>
            <div className="">
              <Label htmlFor="sortCode">Sort Code</Label>
              <Input
                type="text"
                value={data.sortCode}
                disabled={true}
                onChange={handleChange}
                id="sortCode"
              />
            </div>
            <div className="">
              <Label htmlFor="bankAddress">
                Name and full postal address of the bank
              </Label>
              <Textarea
                onChange={handleChange}
                disabled={disableFormCard}
                value={data.bankAddress}
                placeholder="Enter your Bank's Address here."
                id="bankAddress"
              />
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div className="">
              <Label htmlFor="serviceUserNumber">Service User Number</Label>
              <Input
                disabled
                type="text"
                value={data.serviceUserNumber}
                onChange={handleChange}
                id="serviceUserNumber"
              />
            </div>
            <div className="">
              <Label htmlFor="reference">Reference</Label>
              <Input
                disabled
                type="text"
                value={data.reference}
                onChange={handleChange}
                id="reference"
              />
            </div>
            <div>
              <p className="text-xs text-black">
                Instruction to your Bank or Building Society
              </p>
              <p className="text-xs mt-2">
                Please pay {data.originatorCompanyName} direct debits from the
                account detailed in this Instruction subject to the safeguards
                assured by the Direct Debit Guarantee. I understand that this
                Instruction may remain with {data.originatorCompanyName} and, if
                so, details will be passed electronically to my Bank/Building
                Society.
              </p>
            </div>
            <div className="">
              <Label htmlFor="signature">Signature</Label>
              <Input
                type="text"
                value={data.signature}
                disabled={disableFormCard}
                onChange={handleChange}
                id="signature"
              />
            </div>
            <div className="">
              <Label htmlFor="date">Date</Label>
              <Input
                disabled
                type="text"
                value={data.date}
                onChange={handleChange}
                id="date"
              />
            </div>
          </div>
          <div className="flex flex-col space-y-4 col-span-2">
            <div className="flex items-center space-x-2 ">
              <Checkbox
                disabled={disableFormCard}
                id="terms"
                checked={checked}
                onCheckedChange={(checked) => {
                  handleChange(checked, "terms");
                }}
              />
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                I can authorise Direct Debits on this account myself
              </label>
            </div>
            <p>
              Banks and Building Societies may not accept Direct Debit
              Instructions for some types of account
            </p>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex-col gap-6">
        <Separator></Separator>
        <div
          className={
            "flex justify-end gap-2 w-full transition-all duration-500"
          }
        >
          {handleBackButton && (
            <Button variant="yvrSecondary" onClick={handleBackButton}>
              Back
            </Button>
          )}
          {
            <Button
              disabled={!data.bankAddress || !data.signature || disableFormCard}
              variant="yvrPrimary"
              onClick={handleSubmitButton}
            >
              {data.terms ? "Sign Mandate" : "Continue"}
            </Button>
          }
        </div>
      </CardFooter>
    </Card>
  );
};

export default DirectDebitForm;
